import type { ReactElement } from "react";
import {
  Banner,
  Button,
  Card,
  Flex,
  Grid,
  LoadingIndicator,
  Text,
  UnorderedList,
} from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Column, ColumnContainer } from "@jsq/ds/layouts";
import { Colors } from "@jsq/ds/styles";
import { useFeatureFlag } from "@jsq/flags-react";
import { MoneyFormat, MoneyType } from "~/components/Money";
import { formatMoney } from "~/components/Money/utils";
import { type GetEntityPreviewGlSyncQueryResult } from "~/graphql/supergraphHooks";
import {
  type GlSyncTransactionPreview,
  type SyncPositionInfo,
} from "~/graphql/supergraphTypes";
import "~/pages/FundAccounting/Entities/styles_vanilla.css.ts";
import { getCapitalActivityLabel, TransactionCategory } from "~/pages/FundAccounting/types";
import { formatDate, toRoundedCents } from "~/pages/FundAccounting/utils/helpers";
import { SPACING } from "~/styles/variables";
import { generateGpMainUrl, getInvestranBaseUrl } from "~/utils/url";
import {
  columnDefs,
  ColumnItemType,
  detailCellRendererParams,
  gridOptions,
  isRowMaster,
} from "../columnDefs";
import { combinePositionLists, combineUniqueStrings } from "../helpers";
import styles from "../styles";

const existingDataWarningDescriptionNew =
  "Review before proceeding to ensure data will not be duplicated or overwritten:";

type Props = {
  arenaName: string;
  entityName: string | undefined;
  data: GetEntityPreviewGlSyncQueryResult["data"] | null | undefined;
  arenaDomain: string;
  existingTransactionCategories: TransactionCategory[];
  onBack: () => void;
  onStartSync: () => Promise<void> | void;
  requestedTransactionCategories: Set<string>;
  isLoading?: boolean;
  nullJsqPositionIds: string[];
  invalidJsqPositionIds: string[];
};

const buildRowData = (
  transactionData: GlSyncTransactionPreview,
  category: string,
  isNew = false,
) => {
  const totalAmount: MoneyType = {
    amount: toRoundedCents(transactionData.totalAmount),
    precision: 2,
    currency: "USD",
  };
  return {
    category: category,
    glTransactionType: transactionData.transactionTypeName,
    glIntegrationDateRange: `${
      transactionData.startDate ? formatDate(transactionData.startDate) : "Inception"
    } - ${formatDate(transactionData.endDate)}`,
    amount: formatMoney(totalAmount, MoneyFormat.Symbol, false),
    updateCount: {
      count: transactionData.transactions.length.toString(),
      showWarningIcon: !isNew,
    },
    callRecords: transactionData.transactions.map((transaction) => {
      const amount: MoneyType = {
        amount: toRoundedCents(transaction.amount),
        precision: 2,
        currency: "USD",
      };
      return {
        batchId: transaction.batchExternalId,
        glDate: formatDate(transaction.glDate),
        effectiveDate: formatDate(transaction.effectiveDate),
        amount: formatMoney(amount, MoneyFormat.Symbol, false),
        updateSummary: transaction.isDeleted ? "Deleted" : "Changed",
      };
    }),
  };
};

const buildPositionListItem = (position: SyncPositionInfo, positionUrlStub: string) => (
  <li key={position.id}>
    <a
      href={`${positionUrlStub}${position.id}`}
      target="_blank"
      rel="noreferrer"
      css={styles.positionLink}
    >
      {`${position.id}: ${position.name}`}{" "}
      <Icon type="external-link" color={Colors.Primary600} />
    </a>
  </li>
);

const buildPositionMismatchBanner = (
  arenaDomain: string,
  missingPositions: SyncPositionInfo[],
  flaggedZeroOwnershipPositions: SyncPositionInfo[],
) => {
  const missingPositionBannerDescription =
    "The JSQ positions below will not receive transactions in this sync due to missing or mismatched position IDs in the GL. Please review the position IDs in both systems and correct any discrepancies before syncing.";
  const flaggedZeroOwnershipBannerDescription =
    "The following positions in Juniper Square have 0% ownership:";

  const gpMainUrl = generateGpMainUrl(arenaDomain);
  const positionUrlStub = `${gpMainUrl}/position/`;

  return (
    <div>
      {missingPositions.length > 0 && (
        <div>
          <Text marginBottom="md">{missingPositionBannerDescription}</Text>
          <UnorderedList>
            {missingPositions.map((position) =>
              buildPositionListItem(position, positionUrlStub),
            )}
          </UnorderedList>
        </div>
      )}
      {flaggedZeroOwnershipPositions.length > 0 && (
        <div>
          <br />
          <Text marginBottom="md">{flaggedZeroOwnershipBannerDescription}</Text>
          <UnorderedList>
            {flaggedZeroOwnershipPositions.map((position) =>
              buildPositionListItem(position, positionUrlStub),
            )}
          </UnorderedList>
        </div>
      )}
    </div>
  );
};

const buildUnmappedTransactionTypesBanner = (unmappedTransactionTypes: string[]) => {
  return (
    <Flex flexDirection="column" gap={4}>
      <Text>Please review and confirm all unmapped GL transaction types before syncing</Text>
      <Text>The following GL transaction types are unmapped:</Text>
      <UnorderedList>
        {unmappedTransactionTypes.map((transactionType) => (
          <li key={transactionType}>{transactionType}</li>
        ))}
      </UnorderedList>
    </Flex>
  );
};

const renderExistingTransactionDescription = (
  existingTransactionCategories: TransactionCategory[],
  capitalActivityLabel: string,
) => {
  return (
    <div>
      <Text>{existingDataWarningDescriptionNew}</Text>
      <div>
        <UnorderedList>
          {existingTransactionCategories.map((category) => {
            const label =
              category === TransactionCategory.Redemptions ? capitalActivityLabel : category;
            return <li key={category}>{`\u2022 ${label}`}</li>;
          })}
        </UnorderedList>
      </div>
    </div>
  );
};

const INVESTRAN_SPECIFIC_INVESTOR_PANEL_PATH = "#/SpecificInvestorPanel/";

const buildInvalidJsqPositionIdsBannerDescription = (
  invalidJsqPositionIds: string[],
  nullJsqPositionIds: string[],
) => {
  const investranUrl = `${getInvestranBaseUrl()}${INVESTRAN_SPECIFIC_INVESTOR_PANEL_PATH}`;
  return (
    <div>
      <Text>
        Please review and confirm all IDs are accurately reflected in the GL before syncing the
        transactions
      </Text>
      {invalidJsqPositionIds.length > 0 && (
        <div>
          <br />
          <Text>The following specific investors have an invalid JSQ position ID</Text>
          <UnorderedList>
            {invalidJsqPositionIds.map((id) => (
              <li key={id}>
                <a
                  href={`${investranUrl}${id}`}
                  target="_blank"
                  rel="noreferrer"
                  css={styles.positionLink}
                >
                  {id} ↗
                </a>
              </li>
            ))}
          </UnorderedList>
        </div>
      )}
      {nullJsqPositionIds.length > 0 && (
        <div>
          <br />
          <Text>The following specific investors are missing a JSQ position ID</Text>
          <UnorderedList>
            {nullJsqPositionIds.map((id) => (
              <li key={id}>
                <a
                  href={`${investranUrl}${id}`}
                  target="_blank"
                  rel="noreferrer"
                  css={styles.positionLink}
                >
                  {id} ↗
                </a>
              </li>
            ))}
          </UnorderedList>
        </div>
      )}
    </div>
  );
};

const SyncSummary = ({
  arenaName,
  entityName,
  data,
  existingTransactionCategories,
  arenaDomain,
  onBack,
  onStartSync,
  requestedTransactionCategories,
  isLoading = false,
  nullJsqPositionIds,
  invalidJsqPositionIds,
}: Props): ReactElement => {
  const showCapitalActivityLabelCanopy = useFeatureFlag(
    "showCapitalActivityLabelCanopy",
    false,
  );
  const capitalActivityLabel = getCapitalActivityLabel(showCapitalActivityLabelCanopy);

  const priorPeriodBannerTitle = "There are GL transaction updates in the prior period";
  const priorPeriodBannerDescription = `Please review and confirm these transactions are
    accurate in the GL before finishing the update in JSQ.`;

  const positionMismatchBannerTitle = "There are active JSQ positions missing GL transactions";

  const unmappedTransactionTypesBannerTitle = "There are unmapped transactions in the GL";

  const existingDataWarningTitle = `There are existing transactions within the integration date ranges you selected for ${entityName} in ${arenaName}'s JSQ workspace`;

  if (!data) {
    return <LoadingIndicator />;
  }

  // Data returned after we call the backend from the previous step.
  // Finds transactions for categories we requested to sync via checkboxes.
  const previewEntityGLSync = data?.entityPreviewGLSyncNew;

  const missingPositions: SyncPositionInfo[] = combinePositionLists([
    previewEntityGLSync?.capitalCall?.missingPositions || [],
    previewEntityGLSync?.capitalTransaction?.missingPositions || [],
    previewEntityGLSync?.distribution?.missingPositions || [],
    previewEntityGLSync?.netIncome?.missingPositions || [],
  ]);
  const flaggedZeroOwnershipPositions: SyncPositionInfo[] = combinePositionLists([
    previewEntityGLSync?.capitalCall?.flaggedZeroOwnershipPositions || [],
    previewEntityGLSync?.capitalTransaction?.flaggedZeroOwnershipPositions || [],
    previewEntityGLSync?.distribution?.flaggedZeroOwnershipPositions || [],
    previewEntityGLSync?.netIncome?.flaggedZeroOwnershipPositions || [],
  ]);
  const unmappedTransactionTypes: string[] = combineUniqueStrings([
    previewEntityGLSync?.capitalCall?.unmappedTransactionTypes,
    previewEntityGLSync?.capitalTransaction?.unmappedTransactionTypes,
    previewEntityGLSync?.distribution?.unmappedTransactionTypes,
    previewEntityGLSync?.netIncome?.unmappedTransactionTypes,
  ]);

  const rowNewData: ColumnItemType[] = [];
  const rowExistingData: ColumnItemType[] = [];

  // Only add to rowNewData if there are new transactions.
  // New transactions stored under  previewEntityGLSync.category_name.newTransactions.transactions
  let hasNewTransactions = false;
  previewEntityGLSync?.capitalCall?.newTransactions?.forEach((element) => {
    if (element?.transactions) {
      const row = buildRowData(element, "Capital call", true);
      rowNewData.push(row);
      hasNewTransactions = true;
    }
  });
  previewEntityGLSync?.capitalTransaction?.newTransactions?.forEach((element) => {
    if (element?.transactions) {
      const row = buildRowData(element, capitalActivityLabel, true);
      rowNewData.push(row);
      hasNewTransactions = true;
    }
  });
  previewEntityGLSync?.distribution?.newTransactions?.forEach((element) => {
    if (element?.transactions) {
      const row = buildRowData(element, "Distribution", true);
      rowNewData.push(row);
      hasNewTransactions = true;
    }
  });
  previewEntityGLSync?.netIncome?.newTransactions?.forEach((element) => {
    if (element?.transactions) {
      const row = buildRowData(element, "Net income", true);
      rowNewData.push(row);
      hasNewTransactions = true;
    }
  });

  let hasExistingTransactions = false;
  previewEntityGLSync?.capitalCall?.existingTransactions?.forEach((element) => {
    if (element?.transactions) {
      const row = buildRowData(element, "Capital call", true);
      rowExistingData.push(row);
      hasExistingTransactions = true;
    }
  });
  previewEntityGLSync?.capitalTransaction?.existingTransactions?.forEach((element) => {
    if (element?.transactions) {
      const row = buildRowData(element, capitalActivityLabel, true);
      rowExistingData.push(row);
      hasExistingTransactions = true;
    }
  });
  previewEntityGLSync?.distribution?.existingTransactions?.forEach((element) => {
    if (element?.transactions) {
      const row = buildRowData(element, "Distribution", true);
      rowExistingData.push(row);
      hasExistingTransactions = true;
    }
  });
  previewEntityGLSync?.netIncome?.existingTransactions?.forEach((element) => {
    if (element?.transactions) {
      const row = buildRowData(element, "Net income", true);
      rowExistingData.push(row);
      hasExistingTransactions = true;
    }
  });

  const showPriorPeriodBanner = hasExistingTransactions;
  const showPositionMismatchBanner =
    missingPositions.length > 0 || flaggedZeroOwnershipPositions.length > 0;
  const hasUnmappedTransactionTypes = unmappedTransactionTypes.length > 0;
  const shouldShowNewPeriodSummary = hasNewTransactions || hasUnmappedTransactionTypes;
  const positionMismatchBannerDescription = buildPositionMismatchBanner(
    arenaDomain,
    missingPositions,
    flaggedZeroOwnershipPositions,
  );
  const unmappedTransactionTypesBannerDescription =
    buildUnmappedTransactionTypesBanner(unmappedTransactionTypes);

  // 1. Create a Set of categories from dataArray
  // If there are no transactions for a given category, that category name is not added into rowNewData
  const incomingCategories = new Set(rowNewData.map((item) => item.category));

  // 2. Find category names that are not in the requestedTransactionCategories from previous step (checkboxes)
  const missingCategories = [...requestedTransactionCategories].filter(
    (categoryName) => !incomingCategories.has(categoryName),
  );
  const missingCategoriesFormatted = missingCategories
    .map((category) => category.toLowerCase())
    .join(", ");
  const missingCategoriesToSyncTitle = `There are no ${missingCategoriesFormatted} transactions to be synced`;
  const missingCategoriesToSyncDescription = "Go back to edit sync settings";

  const showInvalidJsqPositionIdsBanner =
    invalidJsqPositionIds.length > 0 || nullJsqPositionIds.length > 0;

  return (
    <ColumnContainer>
      <Column size="xl">
        <Flex flexDirection="column">
          {showInvalidJsqPositionIdsBanner && (
            <Banner
              css={styles.warningBanner}
              variant="warning"
              title="There are JSQ IDs in the GL that are missing or invalid"
              description={buildInvalidJsqPositionIdsBannerDescription(
                invalidJsqPositionIds,
                nullJsqPositionIds,
              )}
            />
          )}
          {hasExistingTransactions && (
            <>
              <Card className="inset-lg" title="Prior period transaction summary">
                {showPriorPeriodBanner && (
                  <Banner
                    css={styles.warningBanner}
                    variant="warning"
                    title={priorPeriodBannerTitle}
                    description={priorPeriodBannerDescription}
                  />
                )}

                <Grid
                  className="GridWrapper"
                  css={styles.gridViewport}
                  gridOptions={gridOptions(rowExistingData.length)}
                  rowData={rowExistingData}
                  columnDefs={columnDefs}
                  masterDetail={true}
                  detailCellRendererParams={detailCellRendererParams}
                  isRowMaster={isRowMaster}
                />
              </Card>
            </>
          )}
          {shouldShowNewPeriodSummary ? (
            <>
              <Card className="inset-lg" title="New period transaction summary">
                {hasUnmappedTransactionTypes && (
                  <Banner
                    css={styles.warningBanner}
                    variant="warning"
                    title={unmappedTransactionTypesBannerTitle}
                    description={unmappedTransactionTypesBannerDescription}
                  />
                )}
                {showPositionMismatchBanner && (
                  <Banner
                    css={styles.warningBanner}
                    variant="warning"
                    title={positionMismatchBannerTitle}
                    description={positionMismatchBannerDescription}
                  />
                )}
                {existingTransactionCategories.length > 0 && (
                  <Banner
                    css={styles.warningBanner}
                    variant="warning"
                    title={existingDataWarningTitle}
                    description={renderExistingTransactionDescription(
                      existingTransactionCategories,
                      capitalActivityLabel,
                    )}
                  />
                )}
                {missingCategories.length > 0 && (
                  <Banner
                    css={styles.warningBanner}
                    variant="warning"
                    title={missingCategoriesToSyncTitle}
                    description={missingCategoriesToSyncDescription}
                    buttonProps={{
                      children: "Back to sync settings",
                      onClick: () => {
                        onBack();
                      },
                      variant: "neutral",
                    }}
                  />
                )}

                {hasNewTransactions && (
                  <Grid
                    className="GridWrapper"
                    css={styles.gridViewport}
                    gridOptions={gridOptions(rowNewData.length)}
                    rowData={rowNewData}
                    columnDefs={columnDefs}
                    masterDetail={true}
                    detailCellRendererParams={detailCellRendererParams}
                    isRowMaster={isRowMaster}
                  />
                )}
              </Card>
            </>
          ) : null}
          <Flex css={{ marginTop: SPACING.LARGE }} justifyContent="flex-end">
            <Button css={{ marginRight: SPACING.MEDIUM }} variant="neutral" onClick={onBack}>
              Back
            </Button>
            <Button isLoading={isLoading} onClick={onStartSync}>
              Start sync
            </Button>
          </Flex>
        </Flex>
      </Column>
    </ColumnContainer>
  );
};
export default SyncSummary;
